import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Bell, Inbox, AlertCircle, Sparkles, CheckCircle2, X, Flame, CheckCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { notificationsApi } from "@/lib/api";
import { fmtRelative } from "@/lib/format";
import { cn } from "@/lib/utils";

// Worker returns kind values like "hot_lead" / "approval" / "reply" /
// "task_overdue" / "contract_expiring" / "ai_budget" / "email_opened" /
// "email_clicked". The client-side notificationsApi remaps those to its own
// kind strings; the icon map covers both vocabularies.
const ICON_BY_KIND: Record<string, typeof Bell> = {
  hot_lead: Flame,
  inbox_reply: Inbox,
  reply: Inbox,
  approval: Sparkles,
  task_overdue: AlertCircle,
  contract_expiring: AlertCircle,
  ai_budget: AlertCircle,
  email_opened: CheckCircle2,
  email_clicked: CheckCircle2,
  alert: AlertCircle,
  success: CheckCircle2,
};

export default function NotificationsBell() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => notificationsApi.list(),
    refetchInterval: 30_000,
  });

  const dismiss = useMutation({
    mutationFn: (id: string) => notificationsApi.dismiss(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const dismissAll = useMutation({
    mutationFn: () => notificationsApi.dismissAll(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const items = data?.notifications ?? [];
  const unread = items.filter((n) => !n.read_at).length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="icon" variant="ghost" aria-label="Notifications" className="relative">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-[16px] place-items-center rounded-full bg-danger px-1 text-[10px] font-semibold text-white">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 max-h-[80vh] overflow-y-auto p-0 scrollbar-thin">
        <div className="sticky top-0 flex items-center justify-between gap-2 border-b border-line bg-surface-panel px-3 py-2">
          <span className="text-sm font-semibold text-ink">Notifications</span>
          {items.length > 0 && (
            <button
              onClick={() => dismissAll.mutate()}
              disabled={dismissAll.isPending}
              className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink disabled:opacity-50"
              title="Mark all as read (dismisses every item)"
            >
              <CheckCheck className="h-3 w-3" /> Mark all read
            </button>
          )}
        </div>
        {items.length === 0 ? (
          <p className="px-3 py-8 text-center text-xs text-ink-dim">All caught up.</p>
        ) : (
          <ul className="divide-y divide-line">
            {items.slice(0, 30).map((n) => {
              const Icon = ICON_BY_KIND[n.kind] ?? Bell;
              const accent = n.kind === "hot_lead" ? "text-danger" : "text-brand";
              const inner = (
                <>
                  <Icon className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", n.read_at ? "text-ink-dim" : accent)} />
                  <div className="min-w-0 flex-1">
                    <p className={cn("text-sm leading-tight", n.read_at ? "text-ink-muted" : "font-medium text-ink")}>
                      {n.title}
                    </p>
                    {n.description && (
                      <p className="mt-0.5 line-clamp-2 text-[11px] text-ink-dim">{n.description}</p>
                    )}
                    <p className="mt-0.5 text-[10px] text-ink-dim">{fmtRelative(n.created_at)}</p>
                  </div>
                </>
              );
              const wrapperClass = cn(
                "flex w-full items-start gap-2 px-3 py-2 text-left transition-colors hover:bg-surface-hover",
                !n.read_at && "bg-brand/5",
              );
              return (
                <li key={n.id} className="group relative">
                  {n.action_url ? (
                    <Link to={n.action_url} className={wrapperClass}>
                      {inner}
                    </Link>
                  ) : (
                    <div className={wrapperClass}>{inner}</div>
                  )}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      dismiss.mutate(n.id);
                    }}
                    className="absolute right-2 top-2 rounded p-1 text-ink-dim opacity-0 transition-opacity hover:bg-surface-panel2 hover:text-ink group-hover:opacity-100"
                    aria-label="Dismiss"
                    title="Dismiss"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
